import { useState, useEffect } from "react";

import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { Button } from "V2.0/common/button";
import { CreateDialog } from "./create/CreateDialog";
import httpService from "V2.0/services/httpService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableHeader,
} from "V2.0/common/table";
import { Badge } from "V2.0/common/badge";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Switch } from "../../common/switch";
import { message, Tooltip } from "antd";
import { Heading } from "V2.0/common/heading";
import PipelineDelete from "./PipelineDelete";
import { TiFlowSwitch } from "react-icons/ti";
import { deletePipeline, getPipelines, updatePipeline } from "./service";
import { showErrorToast } from "V2.0/utils";

function formatTimestamp(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);

  const timeDiff = now - date;
  const isWithinDay = timeDiff < 24 * 60 * 60 * 1000;

  const formattedDate = date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return formattedDate;
}

function WorkflowList(props) {
  const { workflows, onSave, fetchPipelines, isViewOnly } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const [openDelete, setOpenDelete] = useState({
    isOpen: false,
    item: null,
  });

  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    message.loading("Deleting Pipeline");

    try {
      const response = await deletePipeline({
        workspaceId: openDelete.item.workspace,
        pipelineId: openDelete.item._id,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Pipeline Deleted");
        fetchPipelines();
        setLoading(false);
        setOpenDelete({ isOpen: false, item: null });
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to delete Pipeline");
      setLoading(false);
    }
  };

  if (workflows?.length > 0) {
    return (
      <>
        <div className="h-[80dvh] overflow-scroll">
          <Table className="">
            <TableHead>
              <TableHeader>Pipeline</TableHeader>
              <TableHeader>Created At</TableHeader>
              <TableHeader>Last Updated At</TableHeader>
              {!isViewOnly && <TableHeader>Actions</TableHeader>}
            </TableHead>
            <TableBody>
              {workflows.map((workflow, idx) => {
                const {
                  _id,
                  name,
                  description,
                  isActive,
                  createdAt,
                  updatedAt,
                } = workflow;
                return (
                  <TableRow
                    className="hover:bg-gray-100 cursor-pointer"
                    onClick={() =>
                      navigate({pathname: `/pipelines/v1/${_id}`, search: location.search}, { state: workflow })
                    }
                  >
                    <TableCell>
                      <div className="flex flex-col gap-y-2">
                        <div className="flex items-start gap-x-2">
                          <p className="font-semibold text-sm">{name}</p>
                          <div>
                            <Switch
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={(checked) => {
                                onSave({ isActive: checked }, _id);
                              }}
                              checked={isActive}
                              color="green"
                              disabled={isViewOnly}
                            />
                          </div>
                        </div>
                        <div className="max-w-[400px]">
                          <Tooltip title={description}>
                            <p className="truncate text-sm text-gray-500">
                              {description}
                            </p>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <p className="text-sm text-gray-500">
                        {formatTimestamp(createdAt)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-500">
                        {formatTimestamp(updatedAt)}
                      </p>
                    </TableCell>
                    {!isViewOnly && <TableCell>
                      <div className="flex gap-x-2">
                        <Button
                          outline
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate({pathname: `/pipelines/${_id}/edit`, search: location.search});
                          }}
                        >
                          <PencilIcon className="size-5" />
                        </Button>
                        <Button
                          outline
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDelete({
                              isOpen: true,
                              item: workflow,
                            });
                          }}
                        >
                          <TrashIcon />
                        </Button>
                      </div>
                    </TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {openDelete.isOpen && (
          <PipelineDelete
            loading={loading}
            pipeline={openDelete.item}
            onClose={() => setOpenDelete({ isOpen: false, item: null })}
            onDelete={handleDelete}
          />
        )}
      </>
    );
  }
}

export function Workflows() {
  const { projectId, isViewOnly } = useOutletContext();
  const [workflows, setWorkflows] = useState({
    isLoading: true,
    data: [],
  });

  const fetchAllWorkflows = async () => {
    try {
      const response = await getPipelines({ workspaceId: projectId });
      const data = response.data;
      setWorkflows({
        isLoading: false,
        data: data.pipelines,
      });
    } catch (e) {
      setWorkflows((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
      showErrorToast(e, "Failed to get pipelines");
    }
  };

  const isNameAlreadyExists = (name) => {
    return workflows.data.some(
      (workflow) => workflow.name.toLowerCase() === name.toLowerCase()
    );
  };

  useEffect(() => {
    if (projectId) {
      fetchAllWorkflows();
    }
  }, [projectId]);

  const handleSaveWorkflow = async (data, id) => {
    message.loading("Updating");
    try {
      const response = await updatePipeline({
        workspaceId: projectId,
        pipelineId: id,
        payload: data,
      });

      if (response.status === 200) {
        message.destroy();
        message.success("Saved");
        fetchAllWorkflows();
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to save changes");
    }
  };

  return (
    <div className="flex h-full max-w-6xl mx-auto py-4">
      <div className="w-full h-full flex flex-col gap-y-8">
        <div className="flex justify-between p-4 items-center">
          <Heading>Pipelines</Heading>
          {!isViewOnly && <div>
            <CreateDialog
              isNameAlreadyExists={isNameAlreadyExists}
              projectId={projectId}
            />
          </div>}
        </div>
        <div className="p-4">
          {!workflows.isLoading &&
            (workflows.data.length === 0 ? (
              <div className="flex h-[40dvh] w-full items-center justify-center mx-auto border rounded-lg">
                <div className="flex flex-col items-center">
                  <TiFlowSwitch className="size-10 mr-1 text-gray-500" />
                  <p className="font-medium text-md text-gray-500">
                    No Pipelines
                  </p>
                </div>
              </div>
            ) : (
              <WorkflowList
                workflows={workflows.data}
                onSave={handleSaveWorkflow}
                fetchPipelines={fetchAllWorkflows}
                isViewOnly={isViewOnly}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
